import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import Modal from '../../../components/Modal/Modal';
import { AppButton } from '../../../components/UI';
import { gomailFetchBlob } from '../api';

const CvPreviewModal = ({ open, candidate, onClose }) => {
  const [state, setState] = useState({ status: 'loading', url: '' });

  const load = useCallback(async () => {
    if (!candidate?.id) return;
    setState({ status: 'loading', url: '' });
    try {
      const blob = await gomailFetchBlob(`/candidates/${candidate.id}/pdf`);
      setState({ status: 'ready', url: URL.createObjectURL(blob) });
    } catch (e) {
      setState({ status: 'error', url: '' });
    }
  }, [candidate?.id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => () => {
    if (state.url) URL.revokeObjectURL(state.url);
  }, [state.url]);

  const download = () => {
    const a = document.createElement('a');
    a.href = state.url;
    a.download = `CV - ${candidate?.full_name || 'candidat'}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const title = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: '#ebf4fb', color: '#03407b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <PictureAsPdfOutlinedIcon />
      </Box>
      <Box>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>CV</Typography>
        <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>{candidate?.full_name}</Typography>
      </Box>
    </Box>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="lg"
      actions={
        <>
          <AppButton variant="secondary" onClick={onClose}>Fermer</AppButton>
          <AppButton variant="outline" startIcon={<OpenInNewIcon />} disabled={state.status !== 'ready'} onClick={() => window.open(state.url, '_blank', 'noopener,noreferrer')}>
            Nouvel onglet
          </AppButton>
          <AppButton startIcon={<FileDownloadIcon />} disabled={state.status !== 'ready'} onClick={download}>
            Télécharger
          </AppButton>
        </>
      }
    >
      <Box sx={{ height: '72vh', borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: '#f1f5f9', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {state.status === 'loading' && (
          <Box sx={{ textAlign: 'center', color: '#64748b' }}>
            <CircularProgress size={34} thickness={4} sx={{ color: '#03407b' }} />
            <Typography sx={{ mt: 1.5, fontSize: '0.85rem' }}>Chargement du CV…</Typography>
          </Box>
        )}
        {state.status === 'error' && (
          <Box sx={{ textAlign: 'center', px: 3 }}>
            <ErrorOutlineIcon sx={{ color: '#dc2626', fontSize: 34 }} />
            <Typography sx={{ mt: 1, fontWeight: 800, color: '#0f172a' }}>Impossible de charger le CV</Typography>
            <AppButton size="small" variant="outline" startIcon={<RefreshIcon />} onClick={load} sx={{ mt: 2 }}>Réessayer</AppButton>
          </Box>
        )}
        {state.status === 'ready' && (
          <iframe title={`CV ${candidate?.full_name || ''}`} src={state.url} style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} />
        )}
      </Box>
    </Modal>
  );
};

export default CvPreviewModal;
